import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';



import { signInRedirectParamKey } from '@asd-stan/auth/domain/constants';
import { getAuthService } from '@asd-stan/auth/infrastructure/getters';
import { MemberScheme } from '@asd-stan/auth/validation-schemas/sign-in.schema';
import { Button } from '@components/button/button';
import { FormError } from '@components/form-error/form-error';
import { FormInput } from '@components/form-input/form-input';
import { FormRadio } from '@components/form-radio/form-radio';
import { ErrorMessage, Field, Form, Formik, FormikHelpers } from 'formik';



import { StyledMember } from '@asd-stan/auth/components/sign-in/member.styled';
import { StyledFieldErrorMessage } from '@components/utility/field-error-message.styled';


type Props = {
	onBack: () => void
}

type MemberFormValues = {
	name: string;
	surname: string;
	email: string;
	company: string;
	country: string;
	engagement: string;
	isLead: string;
	leadMore: string;
	isRegistered: string;
};

export default function MemberForm({ onBack }: Props) {
	const { t } = useTranslation();

	const authService = getAuthService();

	const [isLoading, setLoading] = useState<boolean>(false);
	const [loadingRequest, setLoadingRequest] = useState(false);

	const mapOptionLabel = (group: string, value: string) => {
		if (!value) {
			return value;
		}
		return t(`auth.member.${group}.options.${value}`);
	};

	const initialValues: MemberFormValues = {
		name: '',
		surname: '',
		email: '',
		company: '',
		country: '',
		engagement: '',
		isLead: '',
		leadMore: '',
		isRegistered: '',
	};

	const onSubmit = async (values: MemberFormValues, helpers: FormikHelpers<MemberFormValues>) => {
		try {
			setLoading(true);
			setLoadingRequest(true);

			const payload = {
				...values,
				engagement: mapOptionLabel('engagement', values.engagement),
				isLead: mapOptionLabel('isLead', values.isLead),
				isRegistered: mapOptionLabel('isRegistered', values.isRegistered),
			};

			const res = await authService.submitMemberRequest(payload);
			if (res) {
				helpers.resetForm();
				onBack();
			}
		} catch (error) {
			setLoadingRequest(false);
		} finally {
			setLoading(false);
			setLoadingRequest(false);
		}
	};
	return (
		<>
			<div style={{position: 'relative'}}>
				{isLoading && <div style={{
					position: 'absolute',
					top: 0,
					left: 0,
					width: '100%',
					height: '100%',
					backgroundColor: 'rgba(255,255,255,0.5)',
					zIndex: 1000,
				}}>
					<img src="/static/media/horizontal-loading.42137205d95485b2e8fb.gif" alt="loading" style={{
						position: 'absolute',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						width: 40
					}} />
				</div>}

				<Formik
					initialValues={initialValues}
					onSubmit={onSubmit}
					validationSchema={MemberScheme}
					validateOnBlur={false}
					validateOnChange={false}
				>
					{({ handleSubmit }) => (
						<StyledMember>
							<h1>{t('auth.member.title')}</h1>
							<Form className="member-form">
								<div className="w-half">
									<Field
										component={FormInput}
										name="name"
										title={t('auth.member.name.title')}
										placeholder={t('auth.member.name.placeholder')}
										fullWidth
										showError
									/>
								</div>
								<div className="w-half">
									<Field
										component={FormInput}
										name="surname"
										title={t('auth.member.surname.title')}
										placeholder={t('auth.member.surname.placeholder')}
										fullWidth
										showError
									/>
								</div>

								<Field
									component={FormInput}
									name="email"
									title={t('auth.member.email.title')}
									placeholder={t('auth.member.email.placeholder')}
									fullWidth
									showError
								/>

								<div className="w-half">
									<Field
										component={FormInput}
										name="company"
										title={t('auth.member.company.title')}
										placeholder={t('auth.member.company.placeholder')}
										fullWidth
										showError
									/>
								</div>
								<div className="w-half">
									<Field
										component={FormInput}
										name="country"
										title={t('auth.member.country.title')}
										placeholder={t('auth.member.country.placeholder')}
										fullWidth
										showError
									/>
								</div>

								<div className="radioSection">
									<label title={t('auth.member.engagement.title')}>{ t('auth.member.engagement.title') }</label>
									<div className="radio">
										<Field className="member-field member-field__radio"
										       type="radio"
										       name="engagement"
										       component={FormRadio}
										       label={t('auth.member.engagement.options.active')}
										       value="active"
										/>
									</div>
									<div className="radio">
										<Field className="member-field member-field__radio"
										       type="radio"
										       name="engagement"
										       component={FormRadio}
										       label={t('auth.member.engagement.options.observer')}
										       value="observer"
										/>
									</div>
									<div className="radio">
										<Field className="member-field member-field__radio"
										       type="radio"
										       name="engagement"
										       component={FormRadio}
										       label={t('auth.member.engagement.options.notSure')}
										       value="notSure"
										/>
									</div>

									<ErrorMessage name="engagement" component={StyledFieldErrorMessage} />
								</div>


								<div className="radioSection">
									<label title={t('auth.member.isLead.title')}>{ t('auth.member.isLead.title') }</label>
									<div className="radio">
										<Field className="member-field member-field__radio"
										       type="radio"
										       name="isLead"
										       component={FormRadio}
										       label={t('auth.member.isLead.options.yes') + '*'}
										       value="yes"
										/>
									</div>
									<div className="radio">
										<Field className="member-field member-field__radio"
										       type="radio"
										       name="isLead"
										       component={FormRadio}
										       label={t('auth.member.isLead.options.no')}
										       value="no"
										/>
									</div>
									<div className="radio">
										<Field className="member-field member-field__radio"
										       type="radio"
										       name="isLead"
										       component={FormRadio}
										       label={t('auth.member.isLead.options.notSure')}
										       value="notSure"
										/>
									</div>
									<ErrorMessage name="isLead" component={StyledFieldErrorMessage} />
								</div>

								<Field
									component={FormInput}
									name="leadMore"
									title={'*'+t('auth.member.leadMore.title')}
									placeholder={t('auth.member.leadMore.title')}
									fullWidth
									showError
								/>

								<div className="radioSection">
									<label title={t('auth.member.isRegistered.title')}>{ t('auth.member.isRegistered.title') }</label>
									<div className="radio">
										<Field className="member-field member-field__radio"
										       type="radio"
										       name="isRegistered"
										       component={FormRadio}
										       label={t('auth.member.isRegistered.options.representAsd')}
										       value="representAsd"
										/>
									</div>
									<div className="radio">
										<Field className="member-field member-field__radio"
										       type="radio"
										       name="isRegistered"
										       component={FormRadio}
										       label={t('auth.member.isRegistered.options.member')}
										       value="member"
										/>
									</div>
									<div className="radio">
										<Field className="member-field member-field__radio"
										       type="radio"
										       name="isRegistered"
										       component={FormRadio}
										       label={t('auth.member.isRegistered.options.representOther')}
										       value="representOther"
										/>
									</div>
									<div className="radio">
										<Field className="member-field member-field__radio"
										       type="radio"
										       name="isRegistered"
										       component={FormRadio}
										       label={t('auth.member.isRegistered.options.notSure')}
										       value="notSure"
										/>
									</div>
									<ErrorMessage name="isRegistered" component={StyledFieldErrorMessage} />
								</div>


							</Form>
							<Button
								style={{marginTop: 20}}
								title={t('auth.member.submitionButton')}
								onClick={handleSubmit}
								disabled={loadingRequest}
								fullWidth
							/>
						</StyledMember>
					)}
				</Formik>
			</div>

			<button onClick={onBack} style={{marginTop: 20, marginBottom: 50, background: 'transparent', color: '#666'}}>{t('auth.member.memberBackButton')}</button>
		</>
	);
}